import Bluebird from 'bluebird';
import { Button } from 'components/Button/Button';
import { useLocale } from 'containers/App/LocaleContext';
import withLogError from 'containers/ErrorBoundary/withLogError';
import { showDanger } from 'containers/Toaster/actions';
import DocxGen from 'docxtemplater';
import FileSaver from 'file-saver';
import get from 'lodash/get';
import PizZip from 'pizzip';
import PizZipUtils from 'pizzip/utils';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { withAppProps } from 'utils/react/withAppProps';
import { connectAsAny } from 'utils/redux/connectAsAny';
import { getPieVendorChart } from '../Chartjs';
import { fetchReportData } from './actions';

const loadFile = (url, callback) =>
  PizZipUtils.getBinaryContent(url, (err, data) => callback(err, data));

@withLogError
@withAppProps
class BareDownloadReport extends PureComponent {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    msg: PropTypes.object.isRequired,
    onShowDanger: PropTypes.func.isRequired,
    templateFile: PropTypes.string,
    snapshot: PropTypes.string.isRequired,
    dateFormat: PropTypes.string.isRequired,
    dateTimeFormat: PropTypes.string.isRequired,
    timezone: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
  };

  static defaultProps = {
    templateFile: '/public/reports/analyzeSummary.docx',
  };

  constructor(props) {
    super(props);
    this.state = { loading: false };
    this.dimensions = {
      hostVendorMix: { width: 300, height: 300 },
      networkVendorMix: { width: 300, height: 300 },
    };
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  setState(...args) {
    if (this._isMounted) {
      super.setState(...args);
    }
  }

  getChart() {
    return require('chart.js');
  }

  handleDownloadBtnClick = (e) => {
    e.preventDefault();
    const { snapshot, dispatch, dateFormat, dateTimeFormat, timezone } =
      this.props;
    this.setLoadingState(true);
    return dispatch(
      fetchReportData({
        snapshot,
        dateFormat,
        dateTimeFormat,
        timezone,
        resolve: (response) => this.startRenderReport(response),
        reject: () => this.downloadFinished(),
      }),
    );
  };

  startRenderReport = (response) => {
    const { templateFile, msg } = this.props;
    if (!response || !response.data || !response.charts) {
      return this.handleError(msg.errorNoData);
    }
    this.setLoadingState(true);
    return loadFile(templateFile, (err, content) => {
      if (err) {
        return this.handleError(err);
      }
      return this.genImages(response)
        .then((data) => {
          const ImageModule = require('docxtemplater-image-module');
          const opts = {
            centered: false,
            getImage: (tagValue) => tagValue,
            getSize: (_img, _tagValue, tagName) => {
              if (this.dimensions[tagName]) {
                return [
                  this.dimensions[tagName].width,
                  this.dimensions[tagName].height,
                ];
              }
              return [300, 300]; // First is width, second height
            },
          };
          const zip = new PizZip(content);
          const doc = new DocxGen()
            .loadZip(zip)
            .setOptions({ nullGetter: () => '' });
          doc.attachModule(new ImageModule(opts));
          doc.setData(data);
          doc.render();
          const out = doc.getZip().generate({ type: 'blob' });
          this.setLoadingState(false);
          // !! DO NOT REUSE
          // Downloads should be handled by providing proper headers from backend and initiated using `<a href=""`
          // to use browser's native progress indication and avoid issues with large files.
          // In this case we are building a word document in the browser, which is not a perfect solution at all.
          return FileSaver.saveAs(out, 'networkReport.docx'); // !! DO NOT REUSE
        })
        .catch((e) => {
          return this.handleError(e);
        });
    });
  };

  handleError = (err) => {
    const { onShowDanger } = this.props;
    const message = get(err, 'properties.errors', []).reduce(
      (acc, e) => `${acc} Error: ${e}`,
      '',
    );
    this.setLoadingState(false);
    return onShowDanger({
      message: message.length > 0 ? message : err.message,
      title: message.length > 0 ? null : err.message,
    });
  };

  genImages({ charts, data }) {
    const Chart = this.getChart();
    const { msg } = this.props;

    return Bluebird.reduce(
      Object.keys(charts),
      (acc, key) => {
        switch (key) {
          case 'networkVendorMix':
          case 'hostVendorMix': {
            const result = getPieVendorChart(
              Chart,
              this.canvas,
              msg[key],
              false,
              charts[key].labels ?? [],
              charts[key].data ?? [],
            ).toBase64Image();

            acc[key] = new Buffer(
              result.replace(/^data:image\/\w+;base64,/, ''),
              'base64',
            );
            return acc;
          }
          default:
            return acc;
        }
      },
      {},
    ).then((result) => ({ ...data, ...result }));
  }

  downloadFinished() {
    this.setState({
      loading: false,
    });
  }

  setLoadingState(loading) {
    this.setState({
      loading,
    });
  }

  render() {
    const { loading } = this.state;
    const { disabled } = this.props;
    return (
      <div>
        <canvas
          // something - probably chart.js - messes up inline styles there, adding display: block
          // and making this actually appear on the page, so we need !important style.
          className='!hidden'
          width='300'
          height='300'
          ref={(c) => {
            this.canvas = c;
          }}
        />
        <Button
          type='button'
          variant='secondary'
          size='xxs'
          onClick={this.handleDownloadBtnClick}
          disabled={disabled}
          icon='fa-download'
          loading={loading}
        >
          {this.props.msg.buttonTitle}
        </Button>
      </div>
    );
  }
}

export const DownloadReport = connectAsAny(null, (dispatch) => ({
  onShowDanger: (args) => dispatch(showDanger(args)),
  dispatch,
}))((props) => {
  const downloadReportMsg = useLocale().downloadReport;
  return <BareDownloadReport msg={downloadReportMsg} {...props} />;
});
